import { ChangeDetectorRef, Injectable, inject } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, Observable, EMPTY } from 'rxjs'
import { concatMap, finalize, catchError } from 'rxjs/operators'
import { AppConfigurationService } from '../../../core/services/app-configuration.service'
import { AppSpinnerService } from '../../../core/services/app-spinner.service'
import { HubClientService } from '../../../core/services/hub-client/hub-client.service'
import { MessageService } from '../../../core/services/message.service'
import { SessionTimeoutService } from '../../../core/services/session-timeout.service'
import { LatencyCalculationService } from '../../../latency-tools/services/latency-calculation.service'
import { LotMetrics } from '../../../shared/constants/lot-metrics.constants'
import { PricingOptions } from '../../../core/constants'
import { AuctionHelper } from '../../manage/saleyard/helpers/AuctionHelper'
import { AuctionModel } from '../models/auction.model'
import { LotModel } from '../models/lot.model'
import { AuctionHubService } from './data-services/auction-hub.service'
import { AuctionService } from '../../../shared/services/auction.service'
import { AuctionSaleType } from '../../../core/constants/auction-sale-type.enum'
import { ColumnDefinition } from '../../../core/models/table-definition.model'
import { AuctionTableFactory } from '../factories/auction-table.factory'
import { AUCTION_FORMAT, AUCTION_FORMAT_LABEL } from '../../manage/saleyard/constants/constants'
import * as moment from 'moment'
import objectPath from 'object-path'
import { LotHelper } from '../../manage/saleyard/helpers/LotHelper'
import { EAuctionLotLabel } from 'src/_config/enumerations'

export const tableDataBS = new BehaviorSubject<LotModel[]>([])
export const soldToYouTableDataBS = new BehaviorSubject<LotModel[]>([])
export const canViewBidderDetailsBS = new BehaviorSubject<boolean>(false)
export const transportDrawerOpenBS = new BehaviorSubject<boolean>(false)
export const lotPhotosAndVideosBS = new BehaviorSubject<any>([])
export const auctionLivePrimaryStreamUrlBS = new BehaviorSubject<any>(null)
export const auctionLiveSecondaryStreamUrlBS = new BehaviorSubject<any>(null)
export const hasLiveStreamBS = new BehaviorSubject<boolean>(false)
export const isSaleyardAuctionBS = new BehaviorSubject<boolean | null>(null)
export const auctionLoadingDataBS = new BehaviorSubject<string>('')

function soldToYouLotsUnchanged(prev: LotModel[], next: LotModel[]): boolean {
    if (prev.length !== next.length) {
        return false
    }

    return prev.every((lot, index) => {
        const updated = next[index]
        return (
            lot.id === updated.id &&
            lot.soldPrice === updated.soldPrice &&
            lot.tally === updated.tally &&
            lot.activityStatus === updated.activityStatus
        )
    })
}

@Injectable()
export class AuctionHelperService {
    private auctionService = inject(AuctionService)
    private hubService = inject(HubClientService)
    private activatedRoute = inject(ActivatedRoute)
    private spinner = inject(AppSpinnerService)
    private config = inject(AppConfigurationService)
    private auctionHub = inject(AuctionHubService)
    private latencyCalculationService = inject(LatencyCalculationService)
    private sessionTimeoutService = inject(SessionTimeoutService)
    readonly messageService = inject(MessageService)
    readonly changeDetector = inject(ChangeDetectorRef)
    readonly tableFactory = inject(AuctionTableFactory)

    model: AuctionModel
    subscriptions = <any>[]
    lotMetrics = LotMetrics
    columns: ColumnDefinition[]
    displayedColumns: string[]
    pickChildrenColumns: ColumnDefinition[]
    pickChildrenDisplayedColumns: string[]
    pickChildrenSelectableColumns: ColumnDefinition[]
    runoutChildrenColumns: ColumnDefinition[]
    runoutChildrenDisplayedColumns: string[]
    isMobileResolution = false
    panelOpenState = true
    pricingOption: any
    lotHelper = LotHelper

    constructor() {
        if (window.innerWidth < 850) {
            this.isMobileResolution = true
            this.panelOpenState = false
        }
    }

    init(): void {
        this.loadData()
    }

    get mediaEnabled() {
        return this.model.isNowSelling() || this.model.isLotStarting()
    }

    get lotPhotosAndVideos(): any[] {
        // @@todo: Sort, see 43171
        return objectPath.get(this, 'model.currentLot.media', []).filter((item) => item.isPhoto || item.isVideo)
    }

    get currentLotId(): number {
        return objectPath.get(this, 'model.currentLot.id', null)
    }

    get auctionLivePrimaryStreamUrl() {
        return objectPath.get(this, 'model.primaryStreamUrl', null)
    }

    get auctionLiveSecondaryStreamUrl() {
        return objectPath.get(this, 'model.secondaryStreamUrl', null)
    }

    get hasLiveStream() {
        return this.auctionLivePrimaryStreamUrl || this.auctionLiveSecondaryStreamUrl
    }

    get tableData(): LotModel[] {
        if (!this.model.currentLot) {
            return [...this.model.finishedLots, ...this.model.nextLots].filter(
                (l) => !LotHelper.isPick(l) && !LotHelper.isParentRunoutWithNoTally(l)
            )
        }

        return [
            ...this.model.finishedLots.filter((l) => l.id !== this.model.currentLot!.id),
            { ...this.model.currentLot },
            ...this.model.nextLots
        ].filter((l) => !LotHelper.isPick(l) && !LotHelper.isParentRunoutWithNoTally(l))
    }

    get soldToYouTableData(): LotModel[] {
        const filteredFinishedLots = this.model.finishedLots.filter(
            (l) =>
                !LotHelper.isPick(l) &&
                !LotHelper.isParentRunoutWithNoTally(l) &&
                l.userWonBidding &&
                this.lotHelper.isLotSoldOrPostAuctionSold(l)
        )
        if (
            this.model.currentLot &&
            this.model.currentLot.userWonBidding &&
            this.lotHelper.isLotSoldOrPostAuctionSold(this.model.currentLot)
        ) {
            return [...filteredFinishedLots, this.model.currentLot]
        } else {
            return filteredFinishedLots
        }
    }

    get currentLotChildrenData(): LotModel[] {
        if (!this.lotHelper.isSingleNormalLot(this.model.currentLot!)) {
            return this.lotHelper.getChildLots(this.model.currentLot!, [
                ...this.model.finishedLots,
                ...this.model.nextLots
            ])
        }
        return []
    }

    get auctionNo(): string {
        if (this.model.auction) {
            return `#${this.model.auction.id}`
        }
        return ''
    }

    get auctionTitle(): string {
        if (this.model.auction) {
            return this.model.auction.title
        }
        return ''
    }

    get auctionStatus(): number | undefined {
        return this.model.auction ? this.model.auction.auctionStatus : undefined
    }

    get saleType() {
        const res = this.model.auction ? this.model.auction.saleTypeEnum : undefined
        return res
    }

    get auctionStartsAt(): string {
        if (this.model.auction) {
            return moment.utc(this.model.auction.startsAt).local().format('h:mma')
        }
        return ''
    }

    get isSaleyardAuction(): boolean | null {
        return objectPath.get(this, 'model.auction.auctionFormat') === AUCTION_FORMAT.SALEYARD
    }

    get isSaleyardAuctionLabel(): boolean | null {
        return this.model.auction!.auctionFormatLabel!.toLowerCase() === AUCTION_FORMAT_LABEL.SALEYARD.toLowerCase()
    }

    postAuction(): boolean {
        return !this.model.hasCurrentLot() && this.model.finishedLots.length > 0 && this.auctionStatus === 7
    }

    preAuction(): boolean {
        return (
            !this.model.hasCurrentLot() &&
            (this.isSaleyardAuction || this.model.nextLots.length > 0) &&
            this.auctionStatus !== 7
        )
    }

    getUrlToLot(): string {
        return (
            this.config.getConfig().publicWebsiteUrl +
            '/auction/' +
            this.model.auction!.id +
            '/lot/' +
            this.model.currentLot!.listingId
        )
    }

    getPricingOption(model: AuctionModel): any {
        const currentUnit = model && model.currentLot && model.currentLot.incrementUnits
        const soldUnit = model && model.finishedLots && model.finishedLots[0] && model.finishedLots[0].incrementUnits
        const nextUnit = model && model.nextLots && model.nextLots[0] && model.nextLots[0].incrementUnits
        const unitUsed = nextUnit || currentUnit || soldUnit
        if (unitUsed === PricingOptions.Bale || unitUsed === PricingOptions.Tonne) {
            return { type: 'priceHead', name: PricingOptions[unitUsed] }
        }
        return null
    }

    onBid(amount): void {
        // force sound play for iOS
        const sound = new Audio()
        sound.play()

        // Do not allow next bid until get feedback from server
        this.model.canBid = false
        // Show spinner
        // this.spinner.show()
        // Send signal
        this.auctionHub.sendBid(this.model, amount)
        this.sessionTimeoutService.resetTimeout()
    }

    showTimeEndsSoon() {
        return this.model.timer < 10000 && this.model.isNowSelling()
    }

    disposeSubscriptions(): void {
        this.subscriptions.forEach((s) => {
            s.unsubscribe()
        })
        this.latencyCalculationService.stopMeasurement()
        canViewBidderDetailsBS.next(false)
        transportDrawerOpenBS.next(false)
        soldToYouTableDataBS.next([])
    }

    private publishSoldToYouTableDataIfChanged(): void {
        const next = this.soldToYouTableData
        if (soldToYouLotsUnchanged(soldToYouTableDataBS.value, next)) {
            return
        }
        soldToYouTableDataBS.next(next)
    }

    /**
     * Details/truck icon: approved registered bidder and auction status allows
     * viewing bidder details (includes Finished; writes stay on canChangeBidderDetails).
     */
    private publishCanViewBidderDetails(): void {
        const auctionStatus = this.model?.auction?.auctionStatus
        const canView =
            (this.model?.approvedBidder() ?? false) && AuctionHelper.canViewBidderDetails(auctionStatus)
        if (canViewBidderDetailsBS.value === canView) {
            return
        }
        canViewBidderDetailsBS.next(canView)
        if (!canView && transportDrawerOpenBS.value) {
            transportDrawerOpenBS.next(false)
        }
    }

    private loadData(msg = ''): void {
        if (this.model == null) {
            this.model = AuctionService.initModel()
        }

        const registerHub = 'Register'
        // Show spinner
        this.spinner.show(msg)
        const connectToHub$ = this.hubService
            .connectToHubWithRetry()
            .pipe(
                concatMap((_) => {
                    return this.loadAuction()
                }),
                catchError(() => {
                    this.spinner.hide()
                    auctionLoadingDataBS.next('Error')
                    return EMPTY
                })
            )
            .subscribe({
                next: (data) => {
                    let messageId = data.auction!.messageId

                    const setupHandlers$ = this.auctionHub.setupHub(data)
                    this.subscriptions.push(setupHandlers$)

                    this.subscriptions.push(
                        this.hubService.receiveAsync$.subscribe((r) => {
                            this.publishModelState()

                            messageId = r.Header.MessageId
                        })
                    )

                    this.subscriptions.push(
                        this.hubService.onReconnected$.subscribe((_) => {
                            this.spinner.hide()
                            register(messageId!)
                        })
                    )

                    this.subscriptions.push(
                        this.hubService.onReconnecting$.subscribe((_) => {
                            setTimeout(
                                () => this.spinner.show(this.messageService.getMessage('auction.lostConnectionMsg')),
                                100
                            )

                            this.latencyCalculationService.stopMeasurement()
                        })
                    )

                    const register = (messageId: string) => {
                        this.hubService.callHubMethod(registerHub, data.auction!.id, messageId).subscribe((_) => {
                            this.latencyCalculationService.startMeasurement()
                            this.sessionTimeoutService.resetTimeout()
                        })
                    }

                    register(messageId!)

                    // Setup view model
                    this.model = data

                    this.publishModelState()

                    if (this.model.currentLot != null && this.model.hasAccount()) {
                        // Get autbid
                        const getAutoBid$ = this.auctionService.getAutoBid(this.model).subscribe({
                            next: (r) => {
                                if (r != null) {
                                    // Setup auto bid amount
                                    this.model.currentLot!.autoBid = r.amount
                                    // Do not allow new auto bid
                                    this.model.currentLot!.allowAutoBid = false
                                    // Indicate that auto bid is set
                                    this.model.currentLot!.autoBidIsSet = true
                                }
                            },
                            error: () => {}
                        })
                        this.subscriptions.push(getAutoBid$)
                    }

                    this.pricingOption = this.getPricingOption(this.model)
                },
                error: () => {
                    auctionLoadingDataBS.next('Finished')
                    this.spinner.hide()
                }
            })

        this.subscriptions.push(connectToHub$)
    }

    private loadAuction(): Observable<AuctionModel> {
        return this.activatedRoute.params.pipe(
            concatMap((params) => {
                // Get auction id from query params
                const auctionId = params['id']

                // Load auction details
                let response = this.auctionService.getAuction(auctionId).pipe(
                    finalize(() => {
                        // Hide spinner
                        this.spinner.hide()
                        this.loadTableColumns()
                        this.loadPickChildrenTableColumns()
                    })
                )
                return response
            })
        )
    }

    private publishModelState() {
        tableDataBS.next(this.tableData)
        this.publishSoldToYouTableDataIfChanged()
        this.publishCanViewBidderDetails()
        lotPhotosAndVideosBS.next(this.lotPhotosAndVideos)
        auctionLivePrimaryStreamUrlBS.next(this.auctionLivePrimaryStreamUrl)
        auctionLiveSecondaryStreamUrlBS.next(this.auctionLiveSecondaryStreamUrl)
        hasLiveStreamBS.next(this.hasLiveStream)
        isSaleyardAuctionBS.next(this.isSaleyardAuction)
        auctionLoadingDataBS.next('Finished')
    }

    private loadTableColumns() {
        const unitType = (this.pricingOption && this.pricingOption.type) || null
        const unitName = (this.pricingOption && this.pricingOption.name) || null
        if (this.saleType === AuctionSaleType.Genetics) {
            if (this.isMobileResolution) {
                this.columns = this.tableFactory.geneticMobileTable(unitType, unitName).columns
            } else {
                this.columns = this.tableFactory.geneticTable(unitType, unitName).columns
            }
        } else if (!this.isMobileResolution) {
            this.columns = this.tableFactory.auctionDesktopTable(unitType, unitName).columns
        } else {
            this.columns = this.tableFactory.auctionMobileTable(unitType, unitName).columns
        }
        this.columns = this.columns.map((columnObj) => {
            if (
                columnObj.columnDef === 'lotNumber' &&
                this.model &&
                this.model.auction &&
                this.model.auction!.auctionFormatLabelId === EAuctionLotLabel.Saleyard
            ) {
                columnObj.header = 'Pen no.'
            }
            return columnObj
        })
        this.displayedColumns = this.columns.map((x) => x.columnDef)
    }

    private loadPickChildrenTableColumns() {
        let penOrLot = this.model.auction!.auctionFormatLabelId === EAuctionLotLabel.Saleyard ? 'Pen' : 'Lot'

        if (!this.isMobileResolution) {
            this.pickChildrenColumns = this.tableFactory.auctionPickChildrenDesktopTable(penOrLot).columns
            this.runoutChildrenColumns = this.tableFactory.auctionRunoutChildrenDesktopTable(penOrLot).columns
        } else {
            this.pickChildrenColumns = this.tableFactory.auctionPickChildrenMobileTable(penOrLot).columns
            this.runoutChildrenColumns = this.tableFactory.auctionRunoutChildrenMobileTable(penOrLot).columns
        }
        this.pickChildrenDisplayedColumns = this.pickChildrenColumns.map((x) => x.columnDef)
        this.runoutChildrenDisplayedColumns = this.runoutChildrenColumns.map((x) => x.columnDef)
    }
}
